import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllNewsArticles } from "../../firebase";
import { NewsArticle } from "../../types";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/footer";
import SEOHead from "../../components/SEOHead";

const NewsPage: React.FC = () => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNewsArticles = async () => {
      try {
        setLoading(true);
        // Get all news articles and filter on client side
        const allArticles = await getAllNewsArticles();
        const publishedArticles = allArticles
          .filter((article) => article.published)
          .sort((a, b) => b.createdAt - a.createdAt);
        setArticles(publishedArticles);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching news articles:", err);
        setError("Failed to load news articles. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchNewsArticles();
  }, []);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Get a preview of the article content (first 150 characters)
  const getContentPreview = (content: string) => {
    // Remove HTML tags for the preview
    const plainText = content.replace(/<[^>]+>/g, "");
    return plainText.length > 150
      ? plainText.substring(0, 150) + "..."
      : plainText;
  };

  return (
    <div>
      <Header />
      
      {/* SEO Meta Tags */}
      <SEOHead
        title="Latest News - Wattlenest"
        description="Stay updated with the latest news, events, and updates from the Wattlenest community. Discover stories from athletes, upcoming events, and community highlights."
        url="/news"
        type="website"
      />
      
      <div className="py-52 bg-white">
        <div className="pb-16">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:text-center mb-12">
              <h2 className="text-base font-semibold leading-7 text-gold">
                News
              </h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Latest Updates
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500 mb-4">{error}</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">
                  No news articles available at this time.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {articles.map((article) => (
                  <div
                    key={article.id}
                    className="flex flex-col overflow-hidden rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  >
                    {article.imageURL ? (
                      <div className="flex-shrink-0">
                        <img
                          className="h-48 w-full object-cover"
                          src={article.imageURL}
                          alt={article.title}
                        />
                      </div>
                    ) : (
                      <div className="h-48 w-full bg-gray-200 flex items-center justify-center">
                        <svg
                          className="h-12 w-12 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                          />
                        </svg>
                      </div>
                    )}
                    <div className="flex flex-1 flex-col justify-between bg-white p-6">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gold">
                          {formatDate(article.createdAt)}
                        </p>
                        <Link to={`/news/${article.slug}`} className="mt-2 block">
                          <p className="text-xl font-semibold text-gray-900">
                            {article.title}
                          </p>
                          <p className="mt-3 text-base text-gray-500 line-clamp-3">
                            {getContentPreview(article.body)}
                          </p>
                        </Link>
                      </div>
                      <div className="mt-6">
                        <Link
                          to={`/news/${article.slug}`}
                          className="text-base font-semibold text-gold hover:text-gold-600"
                        >
                          Read more →
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer></Footer>
    </div>
  );
};

export default NewsPage;
